import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Divider,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Banknote, Clock, Fuel, Truck, Wallet, TrendingDown, Landmark, ArrowRightLeft } from 'lucide-react'
import { BudgetBarChart } from '@/components/dashboard/BudgetBarChart'
import { BudgetKpiDateFilterBar, type BudgetKpiFilterState } from '@/components/dashboard/BudgetKpiDateFilterBar'
import { BudgetKpiShiftToggle, type BudgetShiftMode } from '@/components/dashboard/BudgetKpiShiftToggle'
import { StatCard } from '@/components/dashboard/StatCard'
import { DashboardDateFilterBar } from '@/components/dashboard/DashboardDateFilterBar'
import { useAuthStore } from '@/store/auth'
import type { DashboardGetParams } from '@/lib/utils/dashboardQueryParams'
import { serializeDashboardParams } from '@/lib/utils/dashboardQueryParams'
import {
  dashboardService,
  type DashboardBudgetCategoryTotals,
  type DashboardDailyBudgetChartRow,
  type DashboardPayload,
} from '@/lib/services/dashboardService'
import { apiClient } from '@/lib/api/client'
import { format, parse, parseISO } from 'date-fns'

function formatPhp(amount: number): string {
  const hasFraction = !Number.isInteger(amount)
  return `P ${amount.toLocaleString(undefined, {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  })}`
}

function formatCount(n: number): string {
  return n.toLocaleString()
}

const MODAL_PAGE_SIZE = 10

const EMPTY_CATEGORY_TOTALS: DashboardBudgetCategoryTotals = {
  issued_budget: 0,
  truck_trip_budget: 0,
  parts: 0,
  others: 0,
  driver_cash_advance: 0,
  helper_cash_advance: 0,
}

type BudgetSummaryStats = {
  total_budget: number
  total_expense: number
  cash_on_hand: number
  previous_cash_on_hand: number
  category_totals: DashboardBudgetCategoryTotals
  daily_budget_chart: DashboardDailyBudgetChartRow[]
}

function mapCategoryTotals(data: Record<string, unknown>): DashboardBudgetCategoryTotals {
  const source = (data.category_totals ?? {}) as Record<string, unknown>
  return {
    issued_budget: Number(source.issued_budget ?? 0),
    truck_trip_budget: Number(source.truck_trip_budget ?? 0),
    parts: Number(source.parts ?? 0),
    others: Number(source.others ?? 0),
    driver_cash_advance: Number(source.driver_cash_advance ?? 0),
    helper_cash_advance: Number(source.helper_cash_advance ?? 0),
  }
}

function mapDailyBudgetChart(data: unknown): DashboardDailyBudgetChartRow[] {
  if (!Array.isArray(data)) return []
  return data.map((row) => {
    const entry = row as Record<string, unknown>
    return {
      date: String(entry.date ?? ''),
      income: Number(entry.income ?? 0),
      expense: Number(entry.expense ?? 0),
    }
  })
}

function combineCategoryTotals(
  a: DashboardBudgetCategoryTotals,
  b: DashboardBudgetCategoryTotals
): DashboardBudgetCategoryTotals {
  return {
    issued_budget: a.issued_budget + b.issued_budget,
    truck_trip_budget: a.truck_trip_budget + b.truck_trip_budget,
    parts: a.parts + b.parts,
    others: a.others + b.others,
    driver_cash_advance: a.driver_cash_advance + b.driver_cash_advance,
    helper_cash_advance: a.helper_cash_advance + b.helper_cash_advance,
  }
}

function combineDailyBudgetCharts(
  a: DashboardDailyBudgetChartRow[],
  b: DashboardDailyBudgetChartRow[]
): DashboardDailyBudgetChartRow[] {
  const byDate = new Map<string, { income: number; expense: number }>()
  for (const row of [...a, ...b]) {
    const existing = byDate.get(row.date) ?? { income: 0, expense: 0 }
    byDate.set(row.date, {
      income: existing.income + row.income,
      expense: existing.expense + row.expense,
    })
  }

  const orderedDates = a.length > 0 ? a.map((row) => row.date) : b.map((row) => row.date)
  for (const row of b) {
    if (!orderedDates.includes(row.date)) orderedDates.push(row.date)
  }

  return orderedDates.map((date) => ({
    date,
    income: byDate.get(date)?.income ?? 0,
    expense: byDate.get(date)?.expense ?? 0,
  }))
}

function buildBudgetSummaryParams(
  filter: BudgetKpiFilterState,
  shift: 'Day' | 'Night'
) {
  return {
    search: '',
    shift,
    type: 'All',
    transaction_date_from: filter.transaction_date_from,
    transaction_date_to: filter.transaction_date_to,
    dateFilterType: filter.dateFilterType,
  }
}

function mapBudgetSummaryResponse(data: Record<string, unknown>): BudgetSummaryStats {
  return {
    total_budget: Number(data.total_budget ?? 0),
    total_expense: Number(data.total_expense ?? 0),
    cash_on_hand: Number(data.cash_on_hand ?? 0),
    previous_cash_on_hand: Number(data.previous_cash_on_hand ?? 0),
    category_totals: mapCategoryTotals(data),
    daily_budget_chart: mapDailyBudgetChart(data.daily_budget_chart),
  }
}

function combineBudgetSummaryStats(a: BudgetSummaryStats, b: BudgetSummaryStats): BudgetSummaryStats {
  return {
    total_budget: a.total_budget + b.total_budget,
    total_expense: a.total_expense + b.total_expense,
    cash_on_hand: a.cash_on_hand + b.cash_on_hand,
    previous_cash_on_hand: a.previous_cash_on_hand + b.previous_cash_on_hand,
    category_totals: combineCategoryTotals(a.category_totals, b.category_totals),
    daily_budget_chart: combineDailyBudgetCharts(a.daily_budget_chart, b.daily_budget_chart),
  }
}

async function fetchBudgetSummaryStats(
  filter: BudgetKpiFilterState,
  shift: 'Day' | 'Night'
): Promise<BudgetSummaryStats> {
  const response = await apiClient.get('/budget/summary', {
    params: buildBudgetSummaryParams(filter, shift),
  })
  return mapBudgetSummaryResponse(response.data)
}

export default function DashboardPage() {
  const colors = useThemeColors()
  const overdueBgColor = useColorModeValue('red.50', 'gray.800');
  const { user } = useAuthStore()

  const [apiParams, setApiParams] = useState<DashboardGetParams | undefined>(undefined)
  const [dashboardData, setDashboardData] = useState<DashboardPayload | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const overdueModal = useDisclosure()
  const dieselModal = useDisclosure()
  const receivablesModal = useDisclosure()
  const [overdueModalPage, setOverdueModalPage] = useState(1)
  const [dieselModalPage, setDieselModalPage] = useState(1)
  const [receivablesModalPage, setReceivablesModalPage] = useState(1)

  const [budgetFilter, setBudgetFilter] = useState<BudgetKpiFilterState | undefined>(undefined)
  const [budgetShiftMode, setBudgetShiftMode] = useState<BudgetShiftMode>('Day')
  const [budgetStats, setBudgetStats] = useState<BudgetSummaryStats>({
    total_budget: 0,
    total_expense: 0,
    cash_on_hand: 0,
    previous_cash_on_hand: 0,
    category_totals: EMPTY_CATEGORY_TOTALS,
    daily_budget_chart: [],
  })
  const [budgetStatsLoading, setBudgetStatsLoading] = useState(false)

  const handleQueryChange = useCallback((p: DashboardGetParams, _filterKind?: unknown) => {
    setApiParams((prev) => {
      if (prev && serializeDashboardParams(prev) === serializeDashboardParams(p)) {
        return prev
      }
      return p
    })
  }, [])

  const handleBudgetDateRangeChange = useCallback((state: BudgetKpiFilterState) => {
    setBudgetFilter((prev) => {
      if (
        prev &&
        prev.transaction_date_from === state.transaction_date_from &&
        prev.transaction_date_to === state.transaction_date_to &&
        prev.dateFilterType === state.dateFilterType
      ) {
        return prev
      }
      return state
    })
  }, [])

  const budgetFetchKey = useMemo(() => {
    if (!budgetFilter) return null
    return `${budgetFilter.transaction_date_from}:${budgetFilter.transaction_date_to}:${budgetFilter.dateFilterType}:${budgetShiftMode}`
  }, [budgetFilter, budgetShiftMode])

  useEffect(() => {
    if (!budgetFilter || !budgetFetchKey) return
    let cancelled = false
    ;(async () => {
      try {
        setBudgetStatsLoading(true)
        let stats: BudgetSummaryStats
        if (budgetShiftMode === 'Both') {
          const [dayStats, nightStats] = await Promise.all([
            fetchBudgetSummaryStats(budgetFilter, 'Day'),
            fetchBudgetSummaryStats(budgetFilter, 'Night'),
          ])
          stats = combineBudgetSummaryStats(dayStats, nightStats)
        } else {
          stats = await fetchBudgetSummaryStats(budgetFilter, budgetShiftMode)
        }
        if (!cancelled) {
          setBudgetStats(stats)
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching budget stats', error)
        }
      } finally {
        if (!cancelled) setBudgetStatsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [budgetFilter, budgetFetchKey, budgetShiftMode])

  useEffect(() => {
    if (!apiParams) return
    let cancelled = false
    ;(async () => {
      try {
        setIsLoading(true)
        setLoadError(null)
        const data = await dashboardService.getDashboard(apiParams)
        if (!cancelled) setDashboardData(data)
      } catch (e: unknown) {
        if (!cancelled) {
          console.error('Dashboard fetch failed', e)
          setLoadError('Unable to load dashboard data. Please try again.')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiParams])

  const kpis = dashboardData?.kpis

  const overdueRowsAll = useMemo(() => {
    const rows = dashboardData?.overdue_payments ?? []
    return rows.map((r) => ({
      shippingLine: r.shipping_line_name || '—',
      dueDate: (r.due_date || r.overdue_payment_date)
        ? format(parse(r.due_date || r.overdue_payment_date || '', 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')
        : '—',
      soaNo: r.soa_number || r.transaction_no || '—',
      amount: r.overdue_payment_amount ?? 0,
    }))
  }, [dashboardData])
  const overdueRowsPreview = overdueRowsAll.slice(0, 2)

  const dieselByPlateRowsAll = useMemo(() => {
    return (dashboardData?.diesel_by_plate ?? []).map((row) => ({
      plate: row.truck_plate_number || 'Unassigned',
      amount: row.amount ?? 0,
    }))
  }, [dashboardData])
  const dieselByPlateRowsPreview = dieselByPlateRowsAll.slice(0, 6)

  const receivableEntityRowsAll = useMemo(() => {
    return dashboardData?.receivables_by_entity?.entities ?? []
  }, [dashboardData])
  const receivableEntityRowsPreview = receivableEntityRowsAll.slice(0, 6)

  const overdueModalTotalPages = Math.max(1, Math.ceil(overdueRowsAll.length / MODAL_PAGE_SIZE))
  const dieselModalTotalPages = Math.max(1, Math.ceil(dieselByPlateRowsAll.length / MODAL_PAGE_SIZE))
  const receivablesModalTotalPages = Math.max(1, Math.ceil(receivableEntityRowsAll.length / MODAL_PAGE_SIZE))

  const overdueModalSlice = overdueRowsAll.slice(
    (overdueModalPage - 1) * MODAL_PAGE_SIZE,
    overdueModalPage * MODAL_PAGE_SIZE
  )
  const dieselModalSlice = dieselByPlateRowsAll.slice(
    (dieselModalPage - 1) * MODAL_PAGE_SIZE,
    dieselModalPage * MODAL_PAGE_SIZE
  )
  const receivablesModalSlice = receivableEntityRowsAll.slice(
    (receivablesModalPage - 1) * MODAL_PAGE_SIZE,
    receivablesModalPage * MODAL_PAGE_SIZE
  )

  useEffect(() => {
    setOverdueModalPage((p) => Math.min(p, overdueModalTotalPages))
  }, [overdueModalTotalPages])

  useEffect(() => {
    setDieselModalPage((p) => Math.min(p, dieselModalTotalPages))
  }, [dieselModalTotalPages])

  useEffect(() => {
    setReceivablesModalPage((p) => Math.min(p, receivablesModalTotalPages))
  }, [receivablesModalTotalPages])

  const budgetChartProps = useMemo(() => {
    const daily = budgetStats.daily_budget_chart
    if (daily.length === 0) {
      return { labels: [] as string[], income: [] as number[], expenses: [] as number[] }
    }

    return {
      labels: daily.map((d) => format(parseISO(d.date), 'MMM d, yyyy')),
      income: daily.map((d) => d.income ?? 0),
      expenses: daily.map((d) => d.expense ?? 0),
    }
  }, [budgetStats.daily_budget_chart])

  function SummaryCard({
    title,
    value,
    valueLoading,
    icon,
    iconBg,
    iconColor,
    children,
  }: {
    title: string
    value: string
    valueLoading?: boolean
    icon: any
    iconBg: string
    iconColor: string
    children: ReactNode
  }) {
    return (
      <Card
        border="1px solid"
        borderColor={title == "Overdue" ? "red.300": colors.border}
        borderRadius="16px"
        bg={iconBg}
        overflow="hidden"
        boxShadow="sm"
      >
        <CardBody>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3}>
                <Box
                  w={10}
                  h={10}
                  borderRadius="12px"
                  bg={iconColor}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={icon} boxSize={6} color="white" />
                </Box>
                <Box>
                  <Text fontSize="lg" color={colors.textSecondary} fontWeight="medium">
                    {title}
                  </Text>
                </Box>
              </HStack>
            </HStack>
            <Box>
              {valueLoading ? (
                  <Skeleton height="36px" width="140px" mt={1} borderRadius="md" />
                ) : (
                  <Text fontSize="2xl" fontWeight="bold" color={colors.heading}>
                    {value}
                  </Text>
              )}
            </Box>

            {children}
          </VStack>
        </CardBody>
      </Card>
    )
  }

  const overdueDisplay = formatCount(dashboardData?.overdue_count ?? 0)
  const dieselDisplay = formatPhp(kpis?.diesel_expense ?? 0)
  const receivablesDisplay = formatPhp(dashboardData?.receivables_by_entity?.total ?? 0)
  const tripsTotal = kpis?.waybills_total ?? 0
  const tripsDone = kpis?.waybills_completed ?? 0
  const tripsRemaining = kpis?.waybills_remaining ?? 0
  const tripsMainDisplay = formatCount(tripsTotal)
  const budgetCategoryTotals = budgetStats.category_totals
  const summaryValueLoading = isLoading && !kpis
  const receivablesValueLoading = isLoading && !dashboardData

  const budgetStatCards = useMemo(
    () => [
      {
        title: 'Total Budget',
        value: budgetStats.total_budget,
        icon: Wallet,
        color: 'green',
      },
      {
        title: 'Total Expense',
        value: budgetStats.total_expense,
        icon: TrendingDown,
        color: 'red',
      },
      {
        title: 'Cash on Hand',
        value: budgetStats.cash_on_hand,
        icon: Landmark,
        color: 'blue',
      },
      {
        title: 'Cash on Hand (Previous Shift)',
        value: budgetStats.previous_cash_on_hand,
        icon: ArrowRightLeft,
        color: 'purple',
      },
    ],
    [budgetStats, budgetFilter?.dateFilterType]
  )

  return (
    <AdminLayout>
      <Box p={6} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={6} align="stretch" flex="1" overflowY="auto" minH={0}>
          <Box flexShrink={0}>
            <Heading size="lg" mb={1}>
              Welcome back, {user?.name || user?.user_login}!
            </Heading>
            {/* <Text color={colors.text}>
              See the overview/summary of your data in one place.
            </Text> */}
          </Box>

          {loadError && (
            <Text color="red.500" fontSize="sm">
              {loadError}
            </Text>
          )}

          <Box flexShrink={0}>
            <DashboardDateFilterBar onQueryChange={handleQueryChange} />
          </Box>

          {/* Summary Cards */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
            gap={6}
            flexShrink={0}
          >
            <GridItem>
              <SummaryCard
                title="Overdue"
                value={overdueDisplay}
                valueLoading={summaryValueLoading}
                icon={Clock}
                iconBg={overdueBgColor}
                iconColor="red.600"
              >
                <VStack align="stretch" spacing={2}>
                  <SimpleGrid columns={2} fontSize="xs" color={colors.textSecondary} fontWeight="semibold">
                    <Text>Shipping Line</Text>
                    <Text textAlign="right">Due Date</Text>
                  </SimpleGrid>
                  <VStack align="stretch" spacing={2} pt={1}>
                    {isLoading && overdueRowsPreview.length === 0 ? (
                      [...Array(2)].map((_, idx) => (
                        <Box key={idx} borderBottomWidth="1px" borderColor={colors.borderLight} pb={2}>
                          <SimpleGrid columns={2} gap={2}>
                            <Skeleton height="12px" borderRadius="sm" />
                            <Skeleton height="12px" borderRadius="sm" />
                          </SimpleGrid>
                          <Skeleton height="10px" mt={2} width="70%" borderRadius="sm" />
                          <Skeleton height="10px" mt={1} width="45%" borderRadius="sm" />
                        </Box>
                      ))
                    ) : overdueRowsPreview.length === 0 ? (
                      <Text fontSize="xs" color={colors.textMuted}>
                        No overdue items in this range.
                      </Text>
                    ) : (
                      overdueRowsPreview.map((row, idx) => (
                        <Box key={idx} borderBottomWidth="1px" borderColor={colors.borderLight} pb={2}>
                          <SimpleGrid columns={2} fontSize="xs" color={colors.textSecondary} fontWeight="semibold">
                            <Text>{row.shippingLine}</Text>
                            <Text textAlign="right">{row.dueDate}</Text>
                          </SimpleGrid>
                          <Text fontSize="xs" color={colors.textMuted} mt={1}>
                            {row.soaNo}
                          </Text>
                          <Text fontSize="xs" color={colors.textMuted}>
                            {formatPhp(row.amount)}
                          </Text>
                        </Box>
                      ))
                    )}
                  </VStack>
                  {!isLoading && overdueRowsAll.length > 2 ? (
                    <Flex justify="flex-end" pt={1}>
                      <Button
                        variant="link"
                        size="xs"
                        colorScheme="blue"
                        fontWeight="semibold"
                        onClick={() => {
                          setOverdueModalPage(1)
                          overdueModal.onOpen()
                        }}
                      >
                        See more &gt;
                      </Button>
                    </Flex>
                  ) : null}
                </VStack>
              </SummaryCard>
            </GridItem>

            <GridItem>
              <SummaryCard
                title="Diesel Expense"
                value={dieselDisplay}
                valueLoading={summaryValueLoading}
                icon={Fuel}
                iconBg={colors.cardBg}
                iconColor="purple.600"
              >
                <VStack align="stretch" spacing={2}>
                  <SimpleGrid columns={2} fontSize="xs" color={colors.textSecondary} fontWeight="semibold">
                    <Text>Truck Plate #</Text>
                    <Text textAlign="right">Amount</Text>
                  </SimpleGrid>
                  <VStack align="stretch" spacing={1} pt={1}>
                    {isLoading && dieselByPlateRowsPreview.length === 0 ? (
                      [...Array(5)].map((_, idx) => (
                        <SimpleGrid key={idx} columns={2} gap={2}>
                          <Skeleton height="12px" borderRadius="sm" />
                          <Skeleton height="12px" borderRadius="sm" />
                        </SimpleGrid>
                      ))
                    ) : dieselByPlateRowsPreview.length === 0 ? (
                      <Text fontSize="xs" color={colors.textMuted}>
                        No diesel records in this range.
                      </Text>
                    ) : (
                      dieselByPlateRowsPreview.map((row, idx) => (
                        <SimpleGrid key={`${row.plate}-${idx}`} columns={2} fontSize="xs" color={colors.textSecondary}>
                          <Text noOfLines={1}>{row.plate}</Text>
                          <Text textAlign="right">{formatPhp(row.amount)}</Text>
                        </SimpleGrid>
                      ))
                    )}
                  </VStack>
                  {!isLoading && dieselByPlateRowsAll.length > 6 ? (
                    <Flex justify="flex-end" pt={1}>
                      <Button
                        variant="link"
                        size="xs"
                        colorScheme="blue"
                        fontWeight="semibold"
                        onClick={() => {
                          setDieselModalPage(1)
                          dieselModal.onOpen()
                        }}
                      >
                        See more &gt;
                      </Button>
                    </Flex>
                  ) : null}
                </VStack>
              </SummaryCard>
            </GridItem>

            <GridItem>
              <SummaryCard
                title="Receivables"
                value={receivablesDisplay}
                valueLoading={receivablesValueLoading}
                icon={Banknote}
                iconBg={colors.cardBg}
                iconColor="green.400"
              >
                <VStack align="stretch" spacing={2}>
                  <SimpleGrid columns={2} fontSize="xs" color={colors.textSecondary} fontWeight="semibold">
                    <Text>Shipping Line</Text>
                    <Text textAlign="right">Amount</Text>
                  </SimpleGrid>
                  <VStack align="stretch" spacing={1} pt={1}>
                    {isLoading && receivableEntityRowsPreview.length === 0 ? (
                      [...Array(5)].map((_, idx) => (
                        <SimpleGrid key={idx} columns={2} gap={2}>
                          <Skeleton height="12px" borderRadius="sm" />
                          <Skeleton height="12px" borderRadius="sm" />
                        </SimpleGrid>
                      ))
                    ) : receivableEntityRowsPreview.length === 0 ? (
                      <Text fontSize="xs" color={colors.textMuted}>
                        No receivables in this range.
                      </Text>
                    ) : (
                      receivableEntityRowsPreview.map((row, idx) => (
                        <SimpleGrid
                          key={`${row.shipping_line_name ?? 'unknown'}-${idx}`}
                          columns={2}
                          fontSize="xs"
                          color={colors.textSecondary}
                        >
                          <Text noOfLines={1}>{row.shipping_line_name || 'Unknown'}</Text>
                          <Text textAlign="right">{formatPhp(row.amount ?? 0)}</Text>
                        </SimpleGrid>
                      ))
                    )}
                  </VStack>
                  {!isLoading && receivableEntityRowsAll.length > 6 ? (
                    <Flex justify="flex-end" pt={1}>
                      <Button
                        variant="link"
                        size="xs"
                        colorScheme="blue"
                        fontWeight="semibold"
                        onClick={() => {
                          setReceivablesModalPage(1)
                          receivablesModal.onOpen()
                        }}
                      >
                        See more &gt;
                      </Button>
                    </Flex>
                  ) : null}
                </VStack>
              </SummaryCard>
            </GridItem>

            <GridItem>
              <SummaryCard
                title="Truck Trips"
                value={tripsMainDisplay}
                valueLoading={summaryValueLoading}
                icon={Truck}
                iconBg={colors.cardBg}
                iconColor="blue.500"
              >
                <VStack align="stretch" spacing={2}>
                  <VStack align="stretch" spacing={2} pt={1}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color={colors.textSecondary} fontWeight="medium">
                        Completed Trips
                      </Text>
                      {summaryValueLoading ? (
                        <Skeleton height="20px" width="48px" borderRadius="md" />
                      ) : (
                        <Text fontSize="sm" color={colors.heading} fontWeight="semibold">
                          {formatCount(tripsDone)}
                        </Text>
                      )}
                    </Flex>
                    <Divider />
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color={colors.textSecondary} fontWeight="medium">
                        Estimated Remaining Trips
                      </Text>
                      {summaryValueLoading ? (
                        <Skeleton height="20px" width="48px" borderRadius="md" />
                      ) : (
                        <Text fontSize="sm" color={colors.heading} fontWeight="semibold">
                          {formatCount(tripsRemaining)}
                        </Text>
                      )}
                    </Flex>
                  </VStack>
                </VStack>
              </SummaryCard>
            </GridItem>
          </Grid>

          {/* Budget KPI Cards — independent weekly date filter + /budget/summary */}
          <Flex
            flexShrink={0}
            align={{ base: 'stretch', md: 'flex-end' }}
            justify="space-between"
            gap={4}
            flexWrap="wrap"
          >
            <BudgetKpiDateFilterBar onDateRangeChange={handleBudgetDateRangeChange} />
            <BudgetKpiShiftToggle value={budgetShiftMode} onChange={setBudgetShiftMode} />
          </Flex>
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
            gap={6}
            flexShrink={0}
          >
            {budgetStatCards.map((stat) => (
              <GridItem key={stat.title}>
                <StatCard
                  title={stat.title}
                  value={stat.value}
                  icon={stat.icon}
                  color={stat.color}
                  isLoading={budgetStatsLoading}
                />
              </GridItem>
            ))}
          </Grid>

          {/* Budget Summary */}
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(12, 1fr)' }} gap={6} flexShrink={0}>
            <GridItem colSpan={{ base: 12, lg: 9 }}>
              <BudgetBarChart
                title="Budget Summary"
                labels={budgetChartProps.labels}
                income={budgetChartProps.income}
                expenses={budgetChartProps.expenses}
                isLoading={budgetStatsLoading}
              />
            </GridItem>

            <GridItem colSpan={{ base: 12, lg: 3 }}>
              <Card
                border="1px solid"
                borderColor={colors.border}
                borderRadius="16px"
                bg={colors.cardBg}
                boxShadow="sm"
              >
                <CardBody>
                  <VStack align="stretch" spacing={5}>
                    <Box>
                      <HStack spacing={2} mb={2}>
                        <Box w={3} h={3} borderRadius="3px" bg="#3182CE" />
                        <Text fontSize="sm" fontWeight="bold" color={colors.heading}>
                          Budget
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Text fontSize="sm" color={colors.textSecondary} fontWeight="medium">
                          Issued Budget
                        </Text>
                        {budgetStatsLoading ? (
                          <Skeleton ml="auto" height="20px" width="100px" borderRadius="md" />
                        ) : (
                          <Text ml="auto" fontSize="sm" fontWeight="bold" color={colors.heading}>
                            {formatPhp(budgetCategoryTotals.issued_budget)}
                          </Text>
                        )}
                      </HStack>
                    </Box>

                    <Box>
                      <HStack spacing={2} mb={2}>
                        <Box w={3} h={3} borderRadius="3px" bg="#E53E3E" />
                        <Text fontSize="sm" fontWeight="bold" color={colors.heading}>
                          Expenses
                        </Text>
                      </HStack>
                      <VStack align="stretch" spacing={3}>
                        {(
                          [
                            ['Truck Trip', budgetCategoryTotals.truck_trip_budget],
                            ['Parts', budgetCategoryTotals.parts],
                            ['Others', budgetCategoryTotals.others],
                            ['Driver Cash Advance', budgetCategoryTotals.driver_cash_advance],
                            ['Helper Cash Advance', budgetCategoryTotals.helper_cash_advance],
                          ] as const
                        ).map(([label, amount]) => (
                          <HStack key={label} spacing={3}>
                            <Text fontSize="sm" color={colors.textSecondary} fontWeight="medium">
                              {label}
                            </Text>
                            {budgetStatsLoading ? (
                              <Skeleton ml="auto" height="20px" width="88px" borderRadius="md" />
                            ) : (
                              <Text ml="auto" fontSize="sm" fontWeight="bold" color={colors.heading}>
                                {formatPhp(amount)}
                              </Text>
                            )}
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </VStack>
      </Box>

      <Modal isOpen={overdueModal.isOpen} onClose={overdueModal.onClose} size="2xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent bg={colors.cardBg} borderColor={colors.border} borderWidth="1px">
          <ModalHeader color={colors.heading}>Overdue payments</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            <TableContainer maxH="55vh" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th fontWeight="extrabold" color={colors.textSecondary}>Shipping line</Th>
                    <Th fontWeight="extrabold" color={colors.textSecondary}>SOA number</Th>
                    <Th fontWeight="extrabold" isNumeric color={colors.textSecondary}>
                      Amount
                    </Th>
                    <Th fontWeight="extrabold" color={colors.textSecondary}>Due date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {overdueModalSlice.map((row, idx) => (
                    <Tr key={`${row.soaNo}-${idx}`}>
                      <Td color={colors.textSecondary}>{row.shippingLine}</Td>
                      <Td color={colors.textSecondary}>{row.soaNo}</Td>
                      <Td isNumeric color={colors.textSecondary}>
                        {formatPhp(row.amount)}
                      </Td>
                      <Td color={colors.textSecondary}>{row.dueDate}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
          <ModalFooter>
            <HStack justify="space-between" w="100%">
              <Button
                size="sm"
                variant="outline"
                borderColor={colors.border}
                isDisabled={overdueModalPage <= 1}
                onClick={() => setOverdueModalPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Text fontSize="sm" color={colors.textSecondary}>
                Page {overdueModalPage} of {overdueModalTotalPages}
              </Text>
              <Button
                size="sm"
                variant="outline"
                borderColor={colors.border}
                isDisabled={overdueModalPage >= overdueModalTotalPages}
                onClick={() => setOverdueModalPage((p) => p + 1)}
              >
                Next
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={dieselModal.isOpen} onClose={dieselModal.onClose} size="xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent bg={colors.cardBg} borderColor={colors.border} borderWidth="1px">
          <ModalHeader color={colors.heading}>Diesel Expense</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            <TableContainer maxH="55vh" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th fontWeight="extrabold" color={colors.textSecondary}>Truck plate number</Th>
                    <Th fontWeight="extrabold" isNumeric color={colors.textSecondary}>
                      Amount
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dieselModalSlice.map((row, idx) => (
                    <Tr key={`${row.plate}-${idx}`}>
                      <Td color={colors.textSecondary}>{row.plate}</Td>
                      <Td isNumeric color={colors.textSecondary}>
                        {formatPhp(row.amount)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
          <ModalFooter>
            <HStack justify="space-between" w="100%">
              <Button
                size="sm"
                variant="outline"
                borderColor={colors.border}
                isDisabled={dieselModalPage <= 1}
                onClick={() => setDieselModalPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Text fontSize="sm" color={colors.textSecondary}>
                Page {dieselModalPage} of {dieselModalTotalPages}
              </Text>
              <Button
                size="sm"
                variant="outline"
                borderColor={colors.border}
                isDisabled={dieselModalPage >= dieselModalTotalPages}
                onClick={() => setDieselModalPage((p) => p + 1)}
              >
                Next
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={receivablesModal.isOpen} onClose={receivablesModal.onClose} size="xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent bg={colors.cardBg} borderColor={colors.border} borderWidth="1px">
          <ModalHeader color={colors.heading}>Receivables</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            <TableContainer maxH="55vh" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th fontWeight="extrabold" color={colors.textSecondary}>Shipping line</Th>
                    <Th fontWeight="extrabold" isNumeric color={colors.textSecondary}>
                      Amount
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {receivablesModalSlice.map((row, idx) => (
                    <Tr key={`${row.shipping_line_name ?? 'unknown'}-${idx}`}>
                      <Td color={colors.textSecondary}>{row.shipping_line_name || 'Unknown'}</Td>
                      <Td isNumeric color={colors.textSecondary}>
                        {formatPhp(row.amount ?? 0)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
          <ModalFooter>
            <HStack justify="space-between" w="100%">
              <Button
                size="sm"
                variant="outline"
                borderColor={colors.border}
                isDisabled={receivablesModalPage <= 1}
                onClick={() => setReceivablesModalPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Text fontSize="sm" color={colors.textSecondary}>
                Page {receivablesModalPage} of {receivablesModalTotalPages}
              </Text>
              <Button
                size="sm"
                variant="outline"
                borderColor={colors.border}
                isDisabled={receivablesModalPage >= receivablesModalTotalPages}
                onClick={() => setReceivablesModalPage((p) => p + 1)}
              >
                Next
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
